import React, {useMemo} from 'react';
import {Box, Card, Skeleton, Stack, Tooltip, Typography} from '@mui/material';

export interface InfoBlockDataPoint {
    [key: string]: string | number | undefined;
}

export interface InfoBlockData {
    dataKey: string;
    label?: string;
    format?: (value?: string | number) => string;
    compute?: (row: InfoBlockDataPoint) => string | number | undefined;
    tooltip?: React.ReactNode | ((row: InfoBlockDataPoint) => React.ReactNode);
}

export interface InfoBlockConfig {
    points: Array<InfoBlockData>;
}


interface InfoBlockProps {
    data: InfoBlockDataPoint[];
    config: InfoBlockConfig;
    isLoading: boolean;
}

export const InfoBlockWidget: React.FC<InfoBlockProps> = ({
                                                              isLoading,
                                                              config,
                                                              data,
                                                          }) => {

    const firstDataPoint = useMemo(() => data[0], [data]);

    return (
        <>
            <Stack direction={"column"} gap={1}>
                {
                    isLoading &&
                    Array.from({length: config.points?.length || 1}).map((_, e) => (
                        <Skeleton key={`loading-${e}`} sx={{transform: 'none'}} height={44}/>
                    ))
                }
                {
                    !isLoading && (
                        config.points.map((point) => {
                                const row = firstDataPoint ?? {};
                                const value = point.compute
                                    ? point.compute(row)
                                    : firstDataPoint?.[point.dataKey];
                                const formattedValue = point.format ? point.format(value) : value;
                                const label = point.label;
                                const tooltipContent = typeof point.tooltip === 'function'
                                    ? point.tooltip(row)
                                    : point.tooltip;
                                const card = (
                                    <Card
                                        variant="elevation"
                                        sx={{
                                            boxShadow: 'none',
                                            backgroundColor: 'background.paper',
                                            cursor: tooltipContent ? 'help' : 'default',
                                        }}
                                    >
                                        <Box sx={{display: 'flex', flexDirection: 'column'}}>
                                            <Typography variant="body1" component="div" fontWeight={600}>
                                                {formattedValue}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {label ?? ""}
                                            </Typography>
                                        </Box>
                                    </Card>
                                );
                                return tooltipContent
                                    ? (
                                        <Tooltip
                                            key={point.dataKey}
                                            arrow
                                            placement="top"
                                            enterDelay={150}
                                            enterTouchDelay={0}
                                            title={tooltipContent}
                                            slotProps={{
                                                tooltip: {
                                                    sx: {
                                                        px: 1.75,
                                                        py: 1.25,
                                                        maxWidth: 340,
                                                    },
                                                },
                                            }}
                                        >
                                            {card}
                                        </Tooltip>
                                    )
                                    : (
                                        <React.Fragment key={point.dataKey}>
                                            {card}
                                        </React.Fragment>
                                    );
                            }
                        ))
                }
            </Stack>
        </>
    );
};

export default InfoBlockWidget;
